import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getStoredUtm, clearStoredUtm } from "@/hooks/useUtmCapture";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import OnboardingWelcome from "@/components/OnboardingWelcome";

const LEAD_TIME_OPTIONS = [
  { value: "1_day", label: "1 day before" },
  { value: "3_days", label: "3 days before" },
  { value: "1_week", label: "1 week before" },
];

const FEATURE_POINTS = [
  {
    title: "Every occasion remembered",
    desc: "Birthdays, anniversaries, and every moment that matters, never missed.",
    delay: 300,
  },
  {
    title: "Messages that sound like you",
    desc: "The more you use Dearestly, the more personal your messages become.",
    delay: 600,
  },
  {
    title: "A record that grows over time",
    desc: "Notes, history, and care — all in one place.",
    delay: 900,
  },
];

const Onboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [reminderLeadTime, setReminderLeadTime] = useState("3_days");
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(true);

  // Pre-fill first name from OAuth metadata (e.g. Google)
  useEffect(() => {
    const meta = user?.user_metadata;
    if (meta?.given_name || meta?.full_name) {
      setFirstName(meta.given_name || meta.full_name.split(' ')[0]);
    }
  }, [user]);

  // If onboarding is already completed, redirect straight to dashboard
  useEffect(() => {
    if (!user) { setChecking(false); return; }
    supabase
      .from("profiles")
      .select("onboarding_completed")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.onboarding_completed) {
          navigate("/dashboard", { replace: true });
        } else {
          setChecking(false);
        }
      });
  }, [user, navigate]);

  const captureLocation = async (userId: string) => {
    try {
      const res = await fetch("https://ipapi.co/json/", {
        signal: AbortSignal.timeout(3000),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (!data.country_code) return;
      await supabase
        .from("profiles")
        .update({
          country_code: data.country_code,
          city: data.city ?? null,
          location_source: "ip",
        } as any)
        .eq("user_id", userId);
    } catch {
      // Always fail silently
    }
  };

  const setOnboardingComplete = async (userId: string, data: { first_name: string; reminder_lead_time: string }, retries = 3): Promise<boolean> => {
    for (let i = 0; i < retries; i++) {
      const { error } = await supabase
        .from("profiles")
        .update({
          first_name: data.first_name,
          reminder_lead_time: data.reminder_lead_time,
          notification_channels: ["email"],
          onboarding_completed: true,
        } as any)
        .eq("user_id", userId);

      if (!error) return true;

      console.warn(`[onboarding] Attempt ${i + 1} failed for user ${userId}:`, error);
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    console.error(`[onboarding] Failed to set onboarding_completed after ${retries} attempts for user: ${userId}`);
    return false;
  };

  const handleFinish = async () => {
    if (!user) return;
    setSaving(true);

    const trimmedName = firstName.trim().slice(0, 100);

    const success = await setOnboardingComplete(user.id, {
      first_name: trimmedName,
      reminder_lead_time: reminderLeadTime,
    });

    setSaving(false);

    if (!success) {
      toast.error("Something went wrong — please try again.");
    } else {
      // Write UTM params to profile (fire-and-forget)
      const utm = getStoredUtm();
      if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
        supabase
          .from("profiles")
          .update({
            utm_source: utm.utm_source,
            utm_medium: utm.utm_medium,
            utm_campaign: utm.utm_campaign,
          } as any)
          .eq("user_id", user.id)
          .then(() => { clearStoredUtm(); });
      }

      // Fire-and-forget: welcome email + silent geolocation
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "welcome",
          recipientEmail: user.email,
          idempotencyKey: `welcome-${user.id}`,
          templateData: { firstName: trimmedName },
        },
      }).catch(() => {});
      captureLocation(user.id);
      toast.success("You're all set! Welcome to Dearestly");
      navigate("/inbox-check");
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  // Step 0 — full-screen welcome moment (shown only on first onboarding)
  if (step === 0) {
    return <OnboardingWelcome onContinue={() => setStep(1)} />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm">
        {/* Progress indicator */}
        <div className="mb-10 flex items-center justify-center gap-2">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`h-1.5 w-10 rounded-full transition-colors ${
                s <= step ? "bg-accent" : "bg-muted"
              }`}
            />
          ))}
        </div>

        <p className="mb-1 text-center text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Step {step} of 2
        </p>

        {/* Step 1 — Name */}
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h1 className="mb-2 text-center font-serif text-3xl font-semibold text-foreground">
              Welcome to Dearestly
            </h1>
            <p className="mb-8 text-center text-muted-foreground">
              Let's set you up to show up for the people you love.
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">What's your first name?</Label>
                <Input
                  id="firstName"
                  placeholder="e.g. Alex"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={100}
                  className="rounded-full"
                  autoFocus
                />
              </div>
              <Button
                onClick={() => setStep(2)}
                disabled={firstName.trim().length === 0}
                className="w-full rounded-full"
                size="lg"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — Reminder lead time */}
        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <h1 className="mb-2 text-center font-serif text-3xl font-semibold text-foreground">
              How early do you like to be reminded?
            </h1>
            <p className="mb-8 text-center text-muted-foreground">
              We'll remind you ahead of every important date so you always have time to show up.
            </p>
            <div className="space-y-4">
              <Select
                value={reminderLeadTime}
                onValueChange={setReminderLeadTime}
              >
                <SelectTrigger className="rounded-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_TIME_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="w-full rounded-full"
                  size="lg"
                >
                  Back
                </Button>
                <Button
                  onClick={handleFinish}
                  disabled={saving}
                  className="w-full rounded-full"
                  size="lg"
                >
                  {saving ? "Saving…" : "Start showing up →"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
